import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { memberAPI } from '../services/api';
import PaceOfPlaySection from '../components/PaceOfPlaySection';
import BulkPaceStats from '../components/BulkPaceStats';
import EmailHistorySection from '../components/EmailHistorySection';
import './MemberDetails.css';

function MemberDetails() {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [allMembers, setAllMembers] = useState([]);
  const [partnerSearchTerm, setPartnerSearchTerm] = useState('');
  const [showPartnerDropdown, setShowPartnerDropdown] = useState(false);
  const [avoidSearchTerm, setAvoidSearchTerm] = useState('');
  const [showAvoidDropdown, setShowAvoidDropdown] = useState(false);



  // Account management state
  const [hasUserAccount, setHasUserAccount] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);

  // Load member data
  useEffect(() => {
    if (memberId) {
      loadMember();
    }
  }, [memberId]);

  // Load all members for partner selection
  useEffect(() => {
    const loadAllMembers = async () => {
      try {
        const response = await memberAPI.getAll({ limit: 1000 });
        setAllMembers(response.data.data.members || []);
      } catch (error) {
        console.error('Error loading members for typeahead:', error);
      }
    };
    loadAllMembers();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.partner-search')) {
        setShowPartnerDropdown(false);
      }
      if (!event.target.closest('.avoid-search')) {
        setShowAvoidDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadMember = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await memberAPI.getById(memberId);
      const memberData = response.data.data;
      setMember(memberData);
      setHasUserAccount(!!memberData.userId);
      setFormData({
        firstName: memberData.firstName || '',
        lastName: memberData.lastName || '',
        displayName: memberData.displayName || '',
        nickname: memberData.nickname || '',
        ghin: memberData.ghin || '',
        email: memberData.email || '',
        phone: memberData.phone || '',
        handicapIndex: memberData.handicapIndex?.toString() || '',
        membershipStatus: memberData.membershipStatus || 'active',
        membershipType: memberData.membershipType || 'full',
        homeClub: memberData.homeClub || '',
        address: {
          street: memberData.address?.street || '',
          city: memberData.address?.city || '',
          state: memberData.address?.state || '',
          zipCode: memberData.address?.zipCode || ''
        },
        defaultPreferences: {
          timeSlotPreference: memberData.defaultPreferences?.timeSlotPreference || 'no-preference',
          timePreference: memberData.defaultPreferences?.timePreference || 'no-preference',
          playingPartnerPreferences: memberData.defaultPreferences?.playingPartnerPreferences || [],
          avoidPartners: memberData.defaultPreferences?.avoidPartners || [],
          rideOrWalkPreference: memberData.defaultPreferences?.rideOrWalkPreference || 'no-preference',
          corporateAffiliation: {
            company: memberData.defaultPreferences?.corporateAffiliation?.company || '',
            playWithColleagues: memberData.defaultPreferences?.corporateAffiliation?.playWithColleagues || false
          }
        },
        lastMinuteInvitePreferences: {
          acceptInvites: memberData.lastMinuteInvitePreferences?.acceptInvites || false,
          notificationEmail: memberData.lastMinuteInvitePreferences?.notificationEmail || '',
          maxAdvanceNotice: memberData.lastMinuteInvitePreferences?.maxAdvanceNotice || 24,
          inviteStats: memberData.lastMinuteInvitePreferences?.inviteStats || {
            totalInvites: 0,
            acceptedInvites: 0,
            declinedInvites: 0
          }
        },
        notes: memberData.notes || ''
      });
    } catch (err) {
      console.error('Error loading member:', err);
      setError(err.response?.data?.error || 'Failed to load member');
    } finally {
      setLoading(false);
    }
  };

  const handleEditToggle = () => {
    if (editing) {
      // Cancel editing - restore original data
      setEditing(false);
      loadMember(); // Reload to reset form data
    } else {
      setEditing(true);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const submitData = {
        ...formData,
        handicapIndex: parseFloat(formData.handicapIndex) || 0
      };

      await memberAPI.update(member._id, submitData);
      
      // Reload member data and exit edit mode
      await loadMember();
      setEditing(false);
    } catch (err) {
      console.error('Error saving member:', err);
      setError(err.response?.data?.error || 'Failed to save member');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to deactivate ${member.fullName || `${member.firstName} ${member.lastName}`}?`)) {
      return;
    }
    
    try {
      await memberAPI.delete(member._id);
      navigate('/members');
    } catch (err) {
      console.error('Error deleting member:', err);
      alert(err.response?.data?.error || 'Failed to delete member');
    }
  };

  const handleCreateWallet = async () => {
    const initialBalance = parseFloat(prompt(`Create wallet for ${member.firstName} ${member.lastName}?\n\nEnter initial balance (optional, default: $0.00):`)) || 0;
    
    if (initialBalance < 0) {
      alert('Initial balance cannot be negative');
      return;
    }

    try {
      const response = await memberAPI.createMemberWallet(member._id, initialBalance, `Initial wallet for ${member.firstName} ${member.lastName}`);
      alert(`Wallet created successfully!\nInitial balance: $${initialBalance.toFixed(2)}`);
      
      // Reload member data to show the new wallet
      loadMember();
    } catch (err) {
      console.error('Error creating wallet:', err);
      alert(err.response?.data?.error || 'Failed to create wallet');
    }
  };

  const handleSyncWallet = async () => {
    try {
      const response = await memberAPI.syncMemberWallet(member._id);
      const result = response.data.data;

      alert(`Wallet synced successfully!\nCurrent balance: $${result.balance.toFixed(2)}`);

      // Reload member data to show updated wallet
      loadMember();
    } catch (err) {
      console.error('Error syncing wallet:', err);
      alert(err.response?.data?.error || 'Failed to sync wallet');
    }
  };

  // Account management handlers
  const handleCreateAccount = async () => {
    if (!window.confirm(
      `Create login account for ${member.firstName} ${member.lastName}?\n\n` +
      `Default password will be: ${member.lastName.toLowerCase()}${member.lastName.length < 6 ? '123456'.slice(0, 6 - member.lastName.length) : ''}\n\n` +
      `(Minimum 6 characters)`
    )) {
      return;
    }

    try {
      setAccountLoading(true);
      const response = await memberAPI.createAccount(member._id);
      alert(
        `Account created successfully!\n\n` +
        `Username: ${response.data.user.email}\n` +
        `Default Password: ${response.data.defaultPassword}\n\n` +
        `Please share these credentials with the member.`
      );
      setHasUserAccount(true);
      loadMember();
    } catch (err) {
      console.error('Error creating account:', err);
      alert(err.response?.data?.error || 'Failed to create account');
    } finally {
      setAccountLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!window.confirm(
      `Reset password for ${member.firstName} ${member.lastName}?\n\n` +
      `New password will be: ${member.lastName.toLowerCase()}${member.lastName.length < 6 ? '123456'.slice(0, 6 - member.lastName.length) : ''}`
    )) {
      return;
    }

    try {
      setAccountLoading(true);
      const response = await memberAPI.resetPassword(member._id);
      alert(
        `Password reset successfully!\n\n` +
        `New Password: ${response.data.defaultPassword}\n\n` +
        `Please share this with the member.`
      );
    } catch (err) {
      console.error('Error resetting password:', err);
      alert(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setAccountLoading(false);
    }
  };

  // Helper functions for partner selection
  const getFilteredPartners = () => {
    if (!partnerSearchTerm.trim()) return [];
    
    const searchLower = partnerSearchTerm.toLowerCase();
    const selectedGhins = formData.defaultPreferences.playingPartnerPreferences.map(p => p.ghin);
    
    return allMembers
      .filter(m => 
        m.ghin !== formData.ghin && 
        !selectedGhins.includes(m.ghin) &&
        (m.firstName.toLowerCase().includes(searchLower) ||
         m.lastName.toLowerCase().includes(searchLower) ||
         m.ghin.toLowerCase().includes(searchLower))
      )
      .slice(0, 10);
  };

  const addPartner = (partner) => {
    const newPartner = {
      ghin: partner.ghin,
      firstName: partner.firstName,
      lastName: partner.lastName,
      displayName: `${partner.lastName}, ${partner.firstName}`,
      relationship: 'preferred'
    };
    
    handleChange('defaultPreferences.playingPartnerPreferences', [
      ...formData.defaultPreferences.playingPartnerPreferences,
      newPartner
    ]);
    
    setPartnerSearchTerm('');
    setShowPartnerDropdown(false);
  };

  const removePartner = (ghin) => {
    handleChange('defaultPreferences.playingPartnerPreferences',
      formData.defaultPreferences.playingPartnerPreferences.filter(p => p.ghin !== ghin)
    );
  };

  const updatePartnerRelationship = (ghin, relationship) => {
    const updatedPartners = formData.defaultPreferences.playingPartnerPreferences.map(p =>
      p.ghin === ghin ? { ...p, relationship } : p
    );
    handleChange('defaultPreferences.playingPartnerPreferences', updatedPartners);
  };

  // Helper functions for avoid partners
  const getFilteredAvoidPartners = () => {
    if (!avoidSearchTerm.trim()) return [];
    
    const searchLower = avoidSearchTerm.toLowerCase();
    const avoidedGhins = formData.defaultPreferences.avoidPartners.map(p => p.ghin);
    const preferredGhins = formData.defaultPreferences.playingPartnerPreferences.map(p => p.ghin);
    
    return allMembers
      .filter(m => 
        m.ghin !== formData.ghin && 
        !avoidedGhins.includes(m.ghin) &&
        !preferredGhins.includes(m.ghin) &&
        (m.firstName.toLowerCase().includes(searchLower) ||
         m.lastName.toLowerCase().includes(searchLower) ||
         m.ghin.toLowerCase().includes(searchLower))
      )
      .slice(0, 10);
  };

  const addAvoidPartner = (partner) => {
    const newAvoidPartner = {
      ghin: partner.ghin,
      name: `${partner.lastName}, ${partner.firstName}`,
      reason: ''
    };
    
    handleChange('defaultPreferences.avoidPartners', [
      ...formData.defaultPreferences.avoidPartners,
      newAvoidPartner
    ]);
    
    setAvoidSearchTerm('');
    setShowAvoidDropdown(false);
  };

  const removeAvoidPartner = (ghin) => {
    handleChange('defaultPreferences.avoidPartners',
      formData.defaultPreferences.avoidPartners.filter(p => p.ghin !== ghin)
    );
  };

  const updateAvoidReason = (ghin, reason) => {
    const updatedAvoidPartners = formData.defaultPreferences.avoidPartners.map(p =>
      p.ghin === ghin ? { ...p, reason } : p
    );
    handleChange('defaultPreferences.avoidPartners', updatedAvoidPartners);
  };

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child, grandchild] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: grandchild ? {
            ...prev[parent][child],
            [grandchild]: value
          } : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatHandicap = (handicap) => {
    if (handicap === null || handicap === undefined) return 'N/A';
    return handicap.toFixed(1);
  };

  const getStatusBadgeClass = (status) => {
    const baseClass = 'status-badge';
    switch (status) {
      case 'active': return `${baseClass} active`;
      case 'suspended': return `${baseClass} suspended`;
      case 'expired': return `${baseClass} expired`;
      case 'honorary': return `${baseClass} honorary`;
      default: return baseClass;
    }
  };

  if (loading) {
    return (
      <div className="member-details">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading member details...</p>
        </div>
      </div>
    );
  }

  if (error && !member) {
    return (
      <div className="member-details">
        <div className="error-message">
          <p>{error}</p>
          <Link to="/members" className="btn btn-outline">
            Back to Members
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="member-details">
      <div className="back-navigation">
        <Link to="/members" className="back-link">
          ← Back to Members
        </Link>
      </div>

      <div className="member-header">
        <div className="header-content">
          <div className="member-title">
            <h1>{member.lastName}, {member.firstName}</h1>
            {member.nickname && (
              <div className="member-nickname">"{member.nickname}"</div>
            )}
          </div>
          <div className="member-meta">
            <span className={getStatusBadgeClass(member.membershipStatus)}>
              {member.membershipStatus}
            </span>
            <span className="member-type">{member.membershipType}</span>
            <span className="member-ghin">GHIN: {member.ghin}</span>
            <span className="member-handicap">HI: {formatHandicap(member.handicapIndex)}</span>
          </div>
        </div>
        <div className="header-actions">
          {!editing ? (
            <>
              <button className="btn btn-primary" onClick={handleEditToggle}>
                Edit Member
              </button>
              {member.walletId ? (
                <button className="btn btn-secondary" onClick={handleSyncWallet}>
                  Sync Wallet (${(member.walletId.balance || 0).toFixed(2)})
                </button>
              ) : (
                <button className="btn btn-secondary" onClick={handleCreateWallet}>
                  Create Wallet
                </button>
              )}
              {/* Account Management Buttons */}
              {hasUserAccount ? (
                <button
                  className="btn btn-warning"
                  onClick={handleResetPassword}
                  disabled={accountLoading}
                >
                  {accountLoading ? 'Resetting...' : 'Reset Password'}
                </button>
              ) : member.email ? (
                <button
                  className="btn btn-success"
                  onClick={handleCreateAccount}
                  disabled={accountLoading}
                >
                  {accountLoading ? 'Creating...' : 'Create Account'}
                </button>
              ) : null}
              {member.isActive && (
                <button className="btn btn-danger" onClick={handleDelete}>
                  Deactivate
                </button>
              )}
            </>
          ) : (
            <>
              <button className="btn btn-outline" onClick={handleEditToggle}>
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <div className="member-content">
        <div className="content-grid">
          {/* Basic Information Section */}
          <div className="info-section">
            <h3>Basic Information</h3>
            {!editing ? (
              <div className="info-display">
                <div className="info-item">
                  <label>Full Name</label>
                  <span>{member.firstName} {member.lastName}</span>
                </div>
                <div className="info-item">
                  <label>Display Name</label>
                  <span>{member.displayName || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <label>GHIN</label>
                  <span>{member.ghin}</span>
                </div>
                <div className="info-item">
                  <label>Handicap Index</label>
                  <span>{formatHandicap(member.handicapIndex)}</span>
                </div>
                <div className="info-item">
                  <label>Home Club</label>
                  <span>{member.homeClub || 'N/A'}</span>
                </div>
              </div>
            ) : (
              <div className="info-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => handleChange('firstName', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => handleChange('lastName', e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Display Name</label>
                    <input
                      type="text"
                      value={formData.displayName}
                      onChange={(e) => handleChange('displayName', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Nickname</label>
                    <input
                      type="text"
                      value={formData.nickname}
                      onChange={(e) => handleChange('nickname', e.target.value)}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>GHIN *</label>
                    <input
                      type="text"
                      value={formData.ghin}
                      onChange={(e) => handleChange('ghin', e.target.value.toUpperCase())}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Handicap Index *</label>
                    <input
                      type="number"
                      step="0.1"
                      min="-10"
                      max="54"
                      value={formData.handicapIndex}
                      onChange={(e) => handleChange('handicapIndex', e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Home Club</label>
                    <input
                      type="text"
                      value={formData.homeClub}
                      onChange={(e) => handleChange('homeClub', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Contact Information Section */}
          <div className="info-section">
            <h3>Contact Information</h3>
            {!editing ? (
              <div className="info-display">
                <div className="info-item">
                  <label>Email</label>
                  <span>{member.email || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <label>Phone</label>
                  <span>{member.phone || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <label>Address</label>
                  <span>
                    {member.address?.street ? (
                      <>
                        {member.address.street}<br />
                        {member.address.city}, {member.address.state} {member.address.zipCode}
                      </>
                    ) : 'N/A'}
                  </span>
                </div>
              </div>
            ) : (
              <div className="info-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Street Address</label>
                  <input
                    type="text"
                    value={formData.address.street}
                    onChange={(e) => handleChange('address.street', e.target.value)}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      value={formData.address.city}
                      onChange={(e) => handleChange('address.city', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>State</label>
                    <input
                      type="text"
                      value={formData.address.state}
                      onChange={(e) => handleChange('address.state', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Zip Code</label>
                    <input
                      type="text"
                      value={formData.address.zipCode}
                      onChange={(e) => handleChange('address.zipCode', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Membership Information Section */}
          <div className="info-section">
            <h3>Membership</h3>
            {!editing ? (
              <div className="info-display">
                <div className="info-item">
                  <label>Status</label>
                  <span className={getStatusBadgeClass(member.membershipStatus)}>
                    {member.membershipStatus}
                  </span>
                </div>
                <div className="info-item">
                  <label>Type</label>
                  <span className="membership-type">{member.membershipType}</span>
                </div>
                <div className="info-item">
                  <label>Member Since</label>
                  <span>{formatDate(member.createdAt)}</span>
                </div>
                <div className="info-item">
                  <label>Last Updated</label>
                  <span>{formatDate(member.updatedAt)}</span>
                </div>
              </div>
            ) : (
              <div className="info-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      value={formData.membershipStatus}
                      onChange={(e) => handleChange('membershipStatus', e.target.value)}
                    >
                      <option value="active">Active</option>
                      <option value="suspended">Suspended</option>
                      <option value="expired">Expired</option>
                      <option value="honorary">Honorary</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Type</label>
                    <select
                      value={formData.membershipType}
                      onChange={(e) => handleChange('membershipType', e.target.value)}
                    >
                      <option value="full">Full</option>
                      <option value="associate">Associate</option>
                      <option value="junior">Junior</option>
                      <option value="senior">Senior</option>
                      <option value="corporate">Corporate</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Wallet Information Section */}
          {member.walletId && (
            <div className="info-section">
              <h3>Wallet Information</h3>
              <div className="info-display">
                <div className="info-item">
                  <label>Current Balance</label>
                  <span className="wallet-balance">
                    ${(member.walletId.balance || 0).toFixed(2)}
                  </span>
                </div>
                <div className="info-item">
                  <label>Wallet Status</label>
                  <span className={`wallet-status ${member.walletId.isActive ? 'active' : 'inactive'}`}>
                    {member.walletId.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="info-item">
                  <label>Created</label>
                  <span>{formatDate(member.walletId.createdAt)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Account Management Section */}
          <div className="info-section account-management-section">
            <h3>Account Management</h3>
            <div className="info-display">
              <div className="info-item">
                <label>Account Status</label>
                <span className={`account-status ${hasUserAccount ? 'has-account' : 'no-account'}`}>
                  {hasUserAccount ? '✓ Account Active' : '✗ No Account'}
                </span>
              </div>
              <div className="info-item">
                <label>Username (Email)</label>
                <span className="username-display">
                  {member.email || <em className="no-email">No email on file</em>}
                </span>
              </div>
              <div className="info-item">
                <label>Default Password</label>
                <span className="password-formula">
                  {member.lastName ? (
                    <>
                      <code>
                        {member.lastName.toLowerCase()}
                        {member.lastName.length < 6 ? '123456'.slice(0, 6 - member.lastName.length) : ''}
                      </code>
                      <small className="password-note">(lowercase last name, min 6 chars)</small>
                    </>
                  ) : (
                    <em>Cannot generate - no last name</em>
                  )}
                </span>
              </div>
              <div className="account-actions">
                {hasUserAccount ? (
                  <button
                    className="btn btn-warning"
                    onClick={handleResetPassword}
                    disabled={accountLoading}
                  >
                    {accountLoading ? 'Resetting...' : 'Reset Password to Default'}
                  </button>
                ) : member.email ? (
                  <button
                    className="btn btn-success"
                    onClick={handleCreateAccount}
                    disabled={accountLoading}
                  >
                    {accountLoading ? 'Creating...' : 'Create Member Account'}
                  </button>
                ) : (
                  <p className="account-warning">
                    ⚠️ Add an email address to enable account creation
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Order of Merit Section */}
          <div className="info-section order-of-merit-section">
            <h3>Order of Merit</h3>
            <div className="info-display">
              {member.orderOfMerit?.effectiveRank ? (
                <>
                  <div className="oom-effective">
                    <div className="info-item">
                      <label>Current Rank</label>
                      <span className="oom-rank">#{member.orderOfMerit.effectiveRank}</span>
                    </div>
                    <div className="info-item">
                      <label>Effective Points</label>
                      <span className="oom-points">{member.orderOfMerit.effectivePoints?.toFixed(1) || 'N/A'}</span>
                    </div>
                    {member.orderOfMerit.lastCalculated && (
                      <div className="info-item">
                        <label>Last Calculated</label>
                        <span>{formatDate(member.orderOfMerit.lastCalculated)}</span>
                      </div>
                    )}
                  </div>

                  {member.orderOfMerit.history && member.orderOfMerit.history.length > 0 && (
                    <div className="oom-history">
                      <h4>Yearly History</h4>
                      <table className="oom-history-table">
                        <thead>
                          <tr>
                            <th>Year</th>
                            <th>Points</th>
                            <th>Events</th>
                            <th>Rank</th>
                          </tr>
                        </thead>
                        <tbody>
                          {member.orderOfMerit.history
                            .sort((a, b) => b.year - a.year)
                            .map((entry) => (
                              <tr key={entry.year}>
                                <td>{entry.year}</td>
                                <td>{entry.points}</td>
                                <td>{entry.events}</td>
                                <td>{entry.rank ? `#${entry.rank}` : '-'}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              ) : (
                <div className="no-oom-data">
                  <p>No Order of Merit data available for this member.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Playing Preferences Section - Full Width */}
        <div className="info-section full-width">
          <h3>Playing Preferences</h3>
          {!editing ? (
            <div className="preferences-display">
              <div className="preference-item">
                <label>Preferred Tee Time</label>
                <span>{member.defaultPreferences?.timeSlotPreference || 'No Preference'}</span>
              </div>
              <div className="preference-item">
                <label>Ride or Walk</label>
                <span>{member.defaultPreferences?.rideOrWalkPreference || 'No Preference'}</span>
              </div>
              
              {member.defaultPreferences?.playingPartnerPreferences?.length > 0 && (
                <div className="preference-item">
                  <label>Preferred Partners</label>
                  <div className="partner-list">
                    {member.defaultPreferences.playingPartnerPreferences.map((partner, index) => (
                      <span key={index} className="partner-tag">
                        {partner.displayName} ({partner.relationship})
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {member.defaultPreferences?.avoidPartners?.length > 0 && (
                <div className="preference-item">
                  <label>Avoid Partners</label>
                  <div className="avoid-list">
                    {member.defaultPreferences.avoidPartners.map((partner, index) => (
                      <span key={index} className="avoid-tag">
                        {partner.name} {partner.reason && `(${partner.reason})`}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="preferences-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Preferred Tee Time</label>
                  <select
                    value={formData.defaultPreferences.timeSlotPreference}
                    onChange={(e) => handleChange('defaultPreferences.timeSlotPreference', e.target.value)}
                  >
                    <option value="no-preference">No Preference</option>
                    <option value="7:00-7:30">7:00 - 7:30 AM</option>
                    <option value="7:30-8:00">7:30 - 8:00 AM</option>
                    <option value="8:00-8:30">8:00 - 8:30 AM</option>
                    <option value="8:30-9:00">8:30 - 9:00 AM</option>
                    <option value="9:00-9:30">9:00 - 9:30 AM</option>
                    <option value="9:30-10:00">9:30 - 10:00 AM</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Ride or Walk</label>
                  <select
                    value={formData.defaultPreferences.rideOrWalkPreference}
                    onChange={(e) => handleChange('defaultPreferences.rideOrWalkPreference', e.target.value)}
                  >
                    <option value="no-preference">No Preference</option>
                    <option value="ride">Ride (Golf Cart)</option>
                    <option value="walk">Walk</option>
                  </select>
                </div>
              </div>

              {/* Partner Selection - Same as modal */}
              <div className="form-group">
                <label>Playing Partner Preferences</label>
                <div className="partner-selection">
                  <div className="selected-partners">
                    {formData.defaultPreferences.playingPartnerPreferences.map((partner) => (
                      <div key={partner.ghin} className="selected-partner">
                        <span className="partner-name">{partner.displayName}</span>
                        <select
                          className="relationship-select"
                          value={partner.relationship}
                          onChange={(e) => updatePartnerRelationship(partner.ghin, e.target.value)}
                        >
                          <option value="preferred">Preferred</option>
                          <option value="friend">Friend</option>
                          <option value="spouse">Spouse</option>
                          <option value="business">Business</option>
                          <option value="regular-partner">Regular Partner</option>
                        </select>
                        <button
                          type="button"
                          className="remove-partner-btn"
                          onClick={() => removePartner(partner.ghin)}
                          title="Remove partner"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="partner-search">
                    <input
                      type="text"
                      placeholder="Search members to add as preferred partners..."
                      value={partnerSearchTerm}
                      onChange={(e) => {
                        setPartnerSearchTerm(e.target.value);
                        setShowPartnerDropdown(e.target.value.trim().length > 0);
                      }}
                      onFocus={() => setShowPartnerDropdown(partnerSearchTerm.trim().length > 0)}
                    />
                    
                    {showPartnerDropdown && partnerSearchTerm.trim() && (
                      <div className="partner-dropdown">
                        {getFilteredPartners().map((partner) => (
                          <div
                            key={partner.ghin}
                            className="partner-option"
                            onClick={() => addPartner(partner)}
                          >
                            <span className="partner-name">{partner.lastName}, {partner.firstName}</span>
                            <span className="partner-ghin">({partner.ghin})</span>
                          </div>
                        ))}
                        {getFilteredPartners().length === 0 && (
                          <div className="no-partners">No matching members found</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Avoid Partners Selection - Same as modal */}
              <div className="form-group">
                <label>Avoid Partners</label>
                <div className="avoid-selection">
                  <div className="selected-avoid-partners">
                    {formData.defaultPreferences.avoidPartners.map((partner) => (
                      <div key={partner.ghin} className="selected-avoid-partner">
                        <span className="avoid-partner-name">{partner.name}</span>
                        <input
                          type="text"
                          className="avoid-reason-input"
                          placeholder="Optional reason..."
                          value={partner.reason || ''}
                          onChange={(e) => updateAvoidReason(partner.ghin, e.target.value)}
                        />
                        <button
                          type="button"
                          className="remove-avoid-partner-btn"
                          onClick={() => removeAvoidPartner(partner.ghin)}
                          title="Remove from avoid list"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="avoid-search">
                    <input
                      type="text"
                      placeholder="Search members to avoid pairing with..."
                      value={avoidSearchTerm}
                      onChange={(e) => {
                        setAvoidSearchTerm(e.target.value);
                        setShowAvoidDropdown(e.target.value.trim().length > 0);
                      }}
                      onFocus={() => setShowAvoidDropdown(avoidSearchTerm.trim().length > 0)}
                    />
                    
                    {showAvoidDropdown && avoidSearchTerm.trim() && (
                      <div className="avoid-dropdown">
                        {getFilteredAvoidPartners().map((partner) => (
                          <div
                            key={partner.ghin}
                            className="avoid-option"
                            onClick={() => addAvoidPartner(partner)}
                          >
                            <span className="avoid-partner-name">{partner.lastName}, {partner.firstName}</span>
                            <span className="avoid-partner-ghin">({partner.ghin})</span>
                          </div>
                        ))}
                        {getFilteredAvoidPartners().length === 0 && (
                          <div className="no-avoid-partners">No matching members found</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Last-Minute Invite Preferences Section - Full Width */}
        <div className="info-section full-width">
          <h3>🕐 Last-Minute Invite Preferences</h3>
          {editing ? (
            <div className="preferences-form">
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.lastMinuteInvitePreferences.acceptInvites}
                      onChange={(e) => handleChange('lastMinuteInvitePreferences.acceptInvites', e.target.checked)}
                    />
                    Accept Last-Minute Invites
                  </label>
                  <p className="help-text">
                    Get notified when tournament spots become available at the last minute
                  </p>
                </div>
              </div>
              
              {formData.lastMinuteInvitePreferences.acceptInvites && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Notification Email (optional)</label>
                      <input
                        type="email"
                        value={formData.lastMinuteInvitePreferences.notificationEmail}
                        onChange={(e) => handleChange('lastMinuteInvitePreferences.notificationEmail', e.target.value)}
                        placeholder="Leave blank to use primary email"
                      />
                      <p className="help-text">
                        Override primary email for last-minute notifications
                      </p>
                    </div>
                    
                    <div className="form-group">
                      <label>Maximum Advance Notice</label>
                      <select
                        value={formData.lastMinuteInvitePreferences.maxAdvanceNotice}
                        onChange={(e) => handleChange('lastMinuteInvitePreferences.maxAdvanceNotice', parseInt(e.target.value))}
                      >
                        <option value={1}>1 hour</option>
                        <option value={2}>2 hours</option>
                        <option value={4}>4 hours</option>
                        <option value={8}>8 hours</option>
                        <option value={12}>12 hours</option>
                        <option value={24}>24 hours</option>
                        <option value={48}>48 hours</option>
                      </select>
                      <p className="help-text">
                        How far in advance willing to accept invitations
                      </p>
                    </div>
                  </div>
                  
                  {formData.lastMinuteInvitePreferences.inviteStats && 
                   formData.lastMinuteInvitePreferences.inviteStats.totalInvites > 0 && (
                    <div className="invite-stats">
                      <h4>📊 Invite History</h4>
                      <div className="stats-row">
                        <div className="stat-item">
                          <span className="stat-number">{formData.lastMinuteInvitePreferences.inviteStats.totalInvites}</span>
                          <span className="stat-label">Total Invites</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-number">{formData.lastMinuteInvitePreferences.inviteStats.acceptedInvites}</span>
                          <span className="stat-label">Accepted</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-number">{formData.lastMinuteInvitePreferences.inviteStats.declinedInvites}</span>
                          <span className="stat-label">Declined</span>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="preferences-display">
              <div className="preference-item">
                <label>Last-Minute Invites</label>
                <span>
                  {member.lastMinuteInvitePreferences?.acceptInvites ? (
                    <>
                      ✅ Accepts invites up to {member.lastMinuteInvitePreferences.maxAdvanceNotice || 24} hours in advance
                      {member.lastMinuteInvitePreferences.notificationEmail && (
                        <><br />📧 Custom email: {member.lastMinuteInvitePreferences.notificationEmail}</>
                      )}
                    </>
                  ) : (
                    '❌ Not accepting last-minute invites'
                  )}
                </span>
              </div>
              
              {member.lastMinuteInvitePreferences?.inviteStats && 
               member.lastMinuteInvitePreferences.inviteStats.totalInvites > 0 && (
                <div className="preference-item">
                  <label>Invite History</label>
                  <span>
                    {member.lastMinuteInvitePreferences.inviteStats.acceptedInvites} of {member.lastMinuteInvitePreferences.inviteStats.totalInvites} invites accepted
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pace of Play Section - Full Width */}
        <div className="info-section full-width">
          <PaceOfPlaySection 
            member={member}
            onPaceDataUpdated={(result) => {
              console.log('Pace data updated:', result);
            }}
          />
          
          {/* Bulk Pace Analytics */}
          <BulkPaceStats member={member} />
        </div>

        {/* Email History Section - Full Width */}
        <div className="info-section full-width">
          <EmailHistorySection 
            memberId={member._id}
            memberName={`${member.firstName} ${member.lastName}`}
            memberEmail={member.email}
          />
        </div>

        {/* Notes Section - Full Width */}
        <div className="info-section full-width">
          <h3>Notes</h3>
          {!editing ? (
            <div className="notes-display">
              {member.notes ? (
                <p>{member.notes}</p>
              ) : (
                <p className="no-notes">No notes available</p>
              )}
            </div>
          ) : (
            <div className="notes-form">
              <div className="form-group">
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Add notes about this member..."
                  rows="4"
                />
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

export default MemberDetails;